/**
 * KAN-713: чистые функции справочника прокси: форма карточки, тело запроса,
 * тексты проверки и подписи для выбора прокси в формах аккаунта.
 * Разбор строки провайдера и проверка полей: lib/telegramProxy (KAN-655).
 */
import { parseRubAmount, validateRubAmount } from '@/lib/money';
import {
  EMPTY_PROXY_FORM,
  validateProxyForm,
  type ProxyFormValues,
} from '@/lib/telegramProxy';
import type {
  DirectoryProxyType,
  ProxyCheckResult,
  TelegramProxy,
  TelegramProxyBody,
} from '@/types/telegramProxy';

export const DIRECTORY_PROXY_TYPES: ReadonlyArray<{ value: DirectoryProxyType; label: string }> = [
  { value: 'socks5', label: 'SOCKS5' },
  { value: 'http', label: 'HTTP' },
];

export const PROXY_LABEL_MAX = 64;
export const PROXY_COUNTRY_MAX = 64;
export const PROXY_NOTES_MAX = 2000;

/** Подпись выбора в формах аккаунта и кнопка возврата к полям. */
export const DIRECTORY_SELECT_LABEL = 'Прокси из справочника';
export const MANUAL_ENTRY_LABEL = 'ввести вручную';

export interface DirectoryFormValues {
  proxy: ProxyFormValues;
  label: string;
  country: string;
  /** Карточка продавца ("" - не указан). */
  sellerId: string;
  notes: string;
  /** KAN-735: стоимость в месяц, ₽, текстом поля ("" - не задана). */
  monthlyCost: string;
}

export const EMPTY_DIRECTORY_FORM: DirectoryFormValues = {
  proxy: EMPTY_PROXY_FORM,
  label: '',
  country: '',
  sellerId: '',
  notes: '',
  monthlyCost: '',
};

/** Форма из карточки справочника; пароль API не отдаёт, поле пустое. */
export function formFromProxy(p: TelegramProxy): DirectoryFormValues {
  return {
    proxy: {
      type: p.proxy_type,
      host: p.host,
      port: String(p.port),
      username: p.username,
      password: '',
    },
    label: p.label,
    country: p.country,
    sellerId: p.seller_id === null || p.seller_id === undefined ? '' : String(p.seller_id),
    notes: p.notes,
    monthlyCost: p.monthly_cost_rub ? String(p.monthly_cost_rub) : '',
  };
}

/** Тип из списка справочника (SOCKS4 из строки провайдера справочник не принимает). */
function isDirectoryType(type: string): type is DirectoryProxyType {
  return DIRECTORY_PROXY_TYPES.some((t) => t.value === type);
}

/** KAN-735: ошибка поля стоимости или null (пусто - 0). */
export function validateDirectoryCost(v: DirectoryFormValues): string | null {
  return validateRubAmount(v.monthlyCost);
}

/** Ошибка полей подключения или null (для проверки и для сохранения). */
export function validateDirectoryConnection(v: DirectoryFormValues): string | null {
  if (!isDirectoryType(v.proxy.type)) return 'В справочнике только SOCKS5 и HTTP';
  return validateProxyForm(v.proxy);
}

/** Поля подключения для проверки без сохранения. */
export function connectionBody(v: DirectoryFormValues): TelegramProxyBody {
  const body: TelegramProxyBody = {
    proxy_type: v.proxy.type as DirectoryProxyType,
    host: v.proxy.host.trim(),
    port: Number(v.proxy.port),
    username: v.proxy.username.trim(),
  };
  if (v.proxy.password) body.password = v.proxy.password;
  return body;
}

/** Тело POST/PATCH: пустой пароль не передаётся (при изменении остаётся старый). */
export function directoryBody(v: DirectoryFormValues): TelegramProxyBody {
  return {
    ...connectionBody(v),
    label: v.label.trim(),
    country: v.country.trim(),
    seller_id: v.sellerId ? v.sellerId : null,
    notes: v.notes.trim(),
    monthly_cost_rub: parseRubAmount(v.monthlyCost) ?? 0,
  };
}

/**
 * Изменение без нового пароля и с теми же адресом, портом, типом и логином:
 * проверять надо сохранённую карточку (только у неё есть пароль).
 */
export function checksSavedProxy(v: DirectoryFormValues, saved: TelegramProxy | null): boolean {
  if (!saved || v.proxy.password) return false;
  return (
    v.proxy.type === saved.proxy_type &&
    v.proxy.host.trim() === saved.host &&
    Number(v.proxy.port) === saved.port &&
    v.proxy.username.trim() === saved.username
  );
}

/** `host:port` карточки. */
export function proxyAddress(p: Pick<TelegramProxy, 'host' | 'port'>): string {
  return `${p.host}:${p.port}`;
}

/** Текст результата проверки: "Telegram доступен, IP x, 320 мс" или причина отказа. */
export function checkSummary(r: ProxyCheckResult): string {
  if (r.ok) {
    const parts = ['Telegram доступен'];
    if (r.exit_ip) parts.push(`IP ${r.exit_ip}`);
    if (r.latency_ms !== null && r.latency_ms !== undefined) {
      parts.push(`${Math.round(r.latency_ms)} мс`);
    }
    return parts.join(', ');
  }
  if (r.error) return r.error;
  return r.tcp_ok ? 'Прокси отвечает, Telegram через него недоступен' : 'Прокси не отвечает';
}

/** Время проверки "07.10.2026, 14:05" или "" без даты. */
export function formatCheckedAt(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * KAN-715: прокси справочника, на котором сидит аккаунт: по proxy_directory_id,
 * иначе по совпадению host:port с proxy_host строки аккаунта; нет совпадения - null.
 */
export function accountDirectoryProxy(
  account: { proxy_directory_id?: number | null; proxy_host?: string },
  proxies: ReadonlyArray<TelegramProxy>
): TelegramProxy | null {
  const id = account.proxy_directory_id;
  if (id !== null && id !== undefined) {
    const byId = proxies.find((p) => p.id === id);
    if (byId) return byId;
  }
  const host = (account.proxy_host ?? '').trim().toLowerCase();
  if (!host) return null;
  return proxies.find((p) => proxyAddress(p).toLowerCase() === host) ?? null;
}

/** Короткий статус для подписи в выборе: "проверен: работает" / "проверен: ошибка" / "не проверялся". */
function shortStatus(p: TelegramProxy): string {
  if (!p.last_check) return 'не проверялся';
  return p.last_check.ok ? 'работает' : 'ошибка проверки';
}

/** Короткое число аккаунтов на прокси: "2 акк.". */
export function accountsCountLabel(n: number): string {
  return `${n} акк.`;
}

/**
 * KAN-720: подпись прокси в выборе формы аккаунта:
 * "название · host:port · страна · тип · статус · N акк.", пустые части пропускаются,
 * название только если отличается от host:port.
 */
export function proxyOptionLabel(p: TelegramProxy): string {
  const address = proxyAddress(p);
  const label = (p.label ?? '').trim();
  const name = label && label.toLowerCase() !== address.toLowerCase() ? label : '';
  const type = p.proxy_type === 'http' ? 'HTTP' : 'SOCKS5';
  const count = accountsCountLabel(p.accounts?.length ?? 0);
  return [name, address, (p.country ?? '').trim(), type, shortStatus(p), count]
    .filter(Boolean)
    .join(' · ');
}
